import {
  aiFirstCreationStreamingState,
  getProcessedValueKey
} from '@databases/database-ai-first-creation-state';
import {
  type DatabaseStreamHookState,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import type { AiFieldCellRequestTarget } from './cell-request-coordinator/ai-field-cell-request-coordinator';
import { useAiFieldCellRequestState } from './cell-request-coordinator/use-ai-field-cell-request-state';
import { useIsAiSummaryFieldEnabled } from './summary/use-is-ai-summary-field-enabled';

export type AiFieldCellLoadingPhase = 'summarizing' | 'almost-done';
export type AiFieldCellLoadingState = AiFieldCellLoadingPhase | null;

export type UseAiFieldCellLoadingStateOptions = AiFieldCellRequestTarget;

type ActiveAiFieldStream = Extract<
  NonNullable<DatabaseStreamHookState['activeStream']>,
  { kind: 'ai-field' }
>;

export const useAiFieldCellLoadingState = ({
  contentId,
  aiFieldType,
  fieldId,
  entryId
}: UseAiFieldCellLoadingStateOptions): AiFieldCellLoadingState => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();
  const requestState = useAiFieldCellRequestState({
    contentId,
    aiFieldType,
    fieldId,
    entryId
  });
  const { activeStream } = useDatabaseStreamState();
  const { activeAiFieldCell, processedValues } = useSnapshot(
    aiFirstCreationStreamingState
  );
  const almostDoneCellRef = useRef<{
    stream: ActiveAiFieldStream;
    fieldId: string;
    entryId: string;
  } | null>(null);

  if (!isAiSummaryFieldEnabled || aiFieldType !== 'summary') {
    almostDoneCellRef.current = null;
    return null;
  }

  if (activeStream?.kind === 'ai-field') {
    const { entryIds, target } = activeStream;
    const isTargetCell =
      entryIds.has(entryId) &&
      target.aiFieldType === 'summary' &&
      target.fieldId === fieldId &&
      (target.scope === 'field' || target.entryId === entryId);

    if (isTargetCell) {
      if (processedValues.has(getProcessedValueKey(fieldId, entryId))) {
        almostDoneCellRef.current = null;
        return null;
      }

      const isActiveCell =
        activeAiFieldCell?.fieldId === fieldId && activeAiFieldCell.entryId === entryId;
      const hasReachedAlmostDone =
        almostDoneCellRef.current?.stream === activeStream &&
        almostDoneCellRef.current.fieldId === fieldId &&
        almostDoneCellRef.current.entryId === entryId;

      if (isActiveCell) {
        almostDoneCellRef.current = { stream: activeStream, fieldId, entryId };
      }

      if (isActiveCell || hasReachedAlmostDone) {
        return 'almost-done';
      }

      return 'summarizing';
    }
  }

  almostDoneCellRef.current = null;
  return requestState.status === 'idle' ? null : 'summarizing';
};
